import * as React from 'react';

import { TextInput } from '@gitbook/react-uikit';
import {
    Button,
    LoadingIndicator,
    LoadingPanel,
    PageSplitDescription,
    PageSplitTitle,
} from '@gitbook/spine-react';
import { useHeadTitle } from '@gitbook/util-react-head';
import { useLoadingState } from '@gitbook/util-react-hooks';

import { MFAScreenModal } from '../../Components';
import { useHistory, useSetAnalyticsScreen } from '../../navigation';
import { type FirebaseActionURLParams, useAuthOperations } from '../../state';
import { LoginScreenLayout } from './LoginScreenLayout';

/**
 * Step to verify the password reset link.
 */
export function StepApplyPasswordResetLink(props: {
    action: FirebaseActionURLParams;
}): React.ReactElement {
    const { action } = props;
    const { mfa, verifyPasswordResetCode } = useAuthOperations();
    const [loading, wrapLoading] = useLoadingState(true);
    const [resetWithEmail, setResetWithEmail] = React.useState<string | null>(null);

    useHeadTitle('Reset password');
    useSetAnalyticsScreen('login_apply_reset_password');

    React.useEffect(() => {
        wrapLoading(async () => {
            const email = await verifyPasswordResetCode(action.oobCode);

            if (!email) {
                return;
            }

            setResetWithEmail(email);
        });
    }, [action.oobCode]);

    if (mfa) {
        return <MFAScreenModal mfa={mfa} />;
    }

    if (loading) {
        return <LoadingPanel size="large" />;
    }

    if (resetWithEmail) {
        return <StepResetPassword oobCode={action.oobCode} />;
    }

    return (
        <LoginScreenLayout>
            <PageSplitTitle>The password reset link is invalid</PageSplitTitle>
            <PageSplitDescription>
                This can happen if the link is malformed, expired, or has already been used.
            </PageSplitDescription>
        </LoginScreenLayout>
    );
}

/*
 * Step to reset a password.
 */
function StepResetPassword(props: { oobCode: string }): React.ReactElement {
    const { oobCode } = props;

    const { mfa } = useAuthOperations();

    const [reset, setReset] = React.useState(false);

    if (mfa) {
        return <MFAScreenModal mfa={mfa} />;
    }

    return (
        <LoginScreenLayout>
            <PageSplitTitle>Reset your password</PageSplitTitle>
            {reset ? (
                <PasswordIsReset />
            ) : (
                <ChooseNewPassword oobCode={oobCode} onReset={setReset} />
            )}
        </LoginScreenLayout>
    );
}

function PasswordIsReset() {
    const history = useHistory();
    const onGoToSignIn = () => {
        history.push({ pathname: history.location.pathname });
    };
    return (
        <>
            <PageSplitDescription>
                Your password has been reset, you can now login using this new password.
            </PageSplitDescription>
            <Button
                kind="primary"
                onPress={onGoToSignIn}
                label="Continue to login"
                size="large"
                className="w-full"
            />
        </>
    );
}

function ChooseNewPassword(props: { oobCode: string; onReset: (value: boolean) => void }) {
    const { oobCode, onReset } = props;

    const { confirmPasswordReset } = useAuthOperations();

    const [loading, wrapLoading] = useLoadingState();
    const [password, setPassword] = React.useState('');
    const [passwordError, setPasswordError] = React.useState<string | undefined>(undefined);
    const [confirmation, setConfirmation] = React.useState('');
    const [confirmationError, setConfirmationError] = React.useState<string | undefined>(undefined);

    const onSignInWithPassword = async () => {
        wrapLoading(async () => {
            await confirmPasswordReset(oobCode, password);
            onReset(true);
        });
    };

    const onSubmit = () => {
        // Password
        if (password.length < 6) {
            setPasswordError('Your password must be equal or higher to 6 characters.');
            return;
        }
        setPasswordError(undefined);

        // Password's confirmation
        if (password !== confirmation) {
            setConfirmationError('Your confirmation must be the same as the password field.');
            return;
        }
        setConfirmationError(undefined);

        onSignInWithPassword();
    };

    return (
        <>
            <PageSplitDescription>
                Choose a new and secure password to protect your account.
            </PageSplitDescription>

            <div className="flex flex-col gap-4">
                {/* Password */}
                <TextInput
                    testID="login.passwordResetInput"
                    autoFocus
                    label="New password"
                    labelDescription="Your password must be at least 6 characters."
                    value={password}
                    onChangeText={setPassword}
                    feedback={{
                        type: passwordError ? 'danger' : 'none',
                        message: passwordError,
                    }}
                    // @ts-ignore
                    autoComplete="new-password"
                    placeholder="Password"
                    secureTextEntry
                />
                {/* Password confirmation */}
                <TextInput
                    testID="login.passwordResetConfirmationInput"
                    label="Confirm your password"
                    value={confirmation}
                    onChangeText={setConfirmation}
                    feedback={{
                        type: confirmationError ? 'danger' : 'none',
                        message: confirmationError,
                    }}
                    // @ts-ignore
                    autoComplete="new-password"
                    placeholder="Password confirmation"
                    secureTextEntry
                />
                <Button
                    leadingIcon={loading ? LoadingIndicator : undefined}
                    disabled={loading}
                    kind="primary"
                    onPress={onSubmit}
                    label="Update password"
                    size="large"
                    className="w-full"
                />
            </div>
        </>
    );
}
